import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import { NetworkError } from "../../../_layout/genericComponents/Metodos";
import Input from '../../../_layout/genericComponents/Inputs';
import SweetAlert from '../../../_layout/genericComponents/ModalConfirmacion';
import { seleccionEmpresa } from '../../../querys/Empresas';
import { getInicioSiniestrosGraficas } from '../../../querys/InicioSiniestros';
// Redux
import { useSelector } from 'react-redux';
import { danger } from '../../../_layout/helpers/colores';

const useStyles = makeStyles(theme => ({
    root: { flexGrow: 1 },
    paper: { padding: theme.spacing(2), height: '100%' },
    titulo: { textAlign: "left", margin: "0 0 12px 0" },
    // Título con los registros a la izquierda y el monto a la derecha
    encabezado: { display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", columnGap: 12 },
    // Alto fijo para que las cuatro gráficas quepan en la pantalla
    grafica: { position: 'relative', height: 300 },
    sinDatos: { textAlign: "center", color: theme.palette.text.secondary, padding: theme.spacing(6, 0) }
}));

// "Todos los meses" va como "0" (texto): el autocomplete no reconoce una opción con valor 0
const listMeses = [{ label: 'Todos los meses', value: "0" }, ...['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
    .map((label, indice) => ({ label, value: indice + 1 }))];

// Colores por estatus; los que no están en la lista toman uno de la paleta
const COLORES_ESTATUS = {
    'EN PROCESO': 'rgb(54, 162, 235)', 'ABIERTO': 'rgb(255, 159, 64)', 'CERRADO': 'rgb(75, 192, 192)',
    'PAGADO': 'rgb(76, 175, 80)', 'REPARACION': 'rgb(153, 102, 255)', 'REPOSICION': 'rgb(255, 205, 86)',
    'DESISTIDO': 'rgb(201, 203, 207)', 'PRESCRITO': 'rgb(255, 99, 132)'
};
const PALETA = ['rgb(54, 162, 235)', 'rgb(255, 99, 132)', 'rgb(75, 192, 192)', 'rgb(255, 159, 64)', 'rgb(153, 102, 255)', 'rgb(255, 205, 86)', 'rgb(76, 175, 80)', 'rgb(201, 203, 207)', 'rgb(121, 85, 72)'];

// Gráficas en el orden de la pantalla. orden: estatus que siempre se muestran (aunque estén en 0); sin orden, de mayor a menor
const ORDEN_ASEGURADORA = ['ABIERTO', 'EN PROCESO', 'PAGADO', 'CERRADO', 'DESISTIDO', 'PRESCRITO'];
const GRAFICAS = [
    // Por seguimiento del reporte, sin los RECLAMADO (los quita el SP: ya están en las gráficas de reclamos)
    { id: 'REPORTE_SEGUIMIENTO', titulo: 'Reporte de Daños por Estatus', horizontal: true },
    // Los reportes RECLAMADO A ...; el monto es el de sus reclamos, no el costo del daño (lo calcula el SP)
    { id: 'REPORTE_RECLAMOS', titulo: 'Estatus de Reclamos', horizontal: true, orden: ['Siniestros Reclamados a Terceros (RGR-007)', 'Siniestros Reclamados a la Aseguradora (RGR-002)'] },
    { id: 'REPORTE_ESTATUS', titulo: 'Resumen de Reporte de Daños', orden: ['EN PROCESO', 'CERRADO'] },
    { id: 'RECLAMO_ASEGURADORA', titulo: 'Reclamos a la Aseguradora por Estatus', orden: ORDEN_ASEGURADORA },
    { id: 'RECLAMO_TERCEROS', titulo: 'Reclamos a Terceros por Estatus', orden: ['EN PROCESO', 'REPARACION', 'REPOSICION', 'PAGADO', 'CERRADO'] },
    // Todos los estatus por fecha afectado en el periodo del filtro (la de arriba tiene su propia regla)
    { id: 'RECLAMO_ASEGURADORA_PERIODO', titulo: 'Reclamos a la Aseguradora por Estatus', orden: ORDEN_ASEGURADORA }
];

// Montos en pesos: completos en el título y el tooltip; abreviados (mil, M) en las barras
const pesos = (valor) => `$ ${Math.round(valor || 0).toLocaleString('en-US')}`;
function pesosCorto(valor) {
    let numero = Number(valor) || 0;
    if (Math.abs(numero) >= 1000000) return `$ ${(numero / 1000000).toFixed(1)} M`;
    if (Math.abs(numero) >= 1000) return `$ ${(numero / 1000).toFixed(1)} mil`;
    return pesos(numero);
}

function datosGrafica({ id, orden }, registros) {
    let conteo = {}, montos = {};
    registros.filter(({ grafica }) => grafica === id).forEach(({ etiqueta, total, monto }) => {
        conteo[etiqueta] = Number(total) || 0;
        montos[etiqueta] = Number(monto) || 0;
    });
    let etiquetas = orden
        ? [...orden, ...Object.keys(conteo).filter(etiqueta => !orden.includes(etiqueta))]
        : Object.keys(conteo).sort((a, b) => conteo[b] - conteo[a]);
    const sumar = (valores) => etiquetas.reduce((suma, etiqueta) => suma + (valores[etiqueta] || 0), 0);
    return {
        total: sumar(conteo),
        monto: sumar(montos),
        data: {
            labels: etiquetas,
            datasets: [{
                label: 'Registros',
                data: etiquetas.map(etiqueta => conteo[etiqueta] || 0),
                // El monto de cada barra; lo escriben valoresBarras y el tooltip
                montos: etiquetas.map(etiqueta => montos[etiqueta] || 0),
                backgroundColor: etiquetas.map((etiqueta, indice) => COLORES_ESTATUS[etiqueta] || PALETA[indice % PALETA.length])
            }]
        }
    };
}

function opciones(horizontal) {
    const eje = { beginAtZero: true, ticks: { precision: 0 } };
    return {
        indexAxis: horizontal ? 'y' : 'x',
        maintainAspectRatio: false,
        // Espacio para los registros que se escriben afuera, al final de cada barra
        layout: { padding: horizontal ? { right: 34 } : { top: 22 } },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: ({ dataset, dataIndex }) => [`Registros: ${dataset.data[dataIndex]}`, `Monto: ${pesos(dataset.montos[dataIndex])}`]
                }
            }
        },
        scales: horizontal ? { x: eje } : { y: eje }
    };
}

// Texto blanco sobre colores oscuros y oscuro sobre claros (color "rgb(r, g, b)")
function colorTexto(fondo) {
    let [r, g, b] = (String(fondo).match(/\d+/g) || [0, 0, 0]).map(Number);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#333333' : '#FFFFFF';
}

// Escribe en cada barra los registros (en negritas) afuera, al final de la barra, y el monto adentro:
// horizontales a la derecha, verticales arriba. Si el monto no cabe en la barra se escribe afuera,
// junto a los registros
const valoresBarras = {
    id: 'valoresBarras',
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const horizontal = chart.options.indexAxis === 'y';
        const negritas = 'bold 12px Arial', normal = '11px Arial';
        ctx.save();
        chart.data.datasets.forEach((dataset, indice) => {
            chart.getDatasetMeta(indice).data.forEach((barra, posicion) => {
                const registros = String(dataset.data[posicion]);
                const monto = pesosCorto((dataset.montos || [])[posicion]);
                const fondo = Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[posicion] : dataset.backgroundColor;
                ctx.font = negritas;
                let anchoRegistros = ctx.measureText(registros).width;
                ctx.font = normal;
                let anchoMonto = ctx.measureText(monto).width;
                let cabe = horizontal
                    ? Math.abs(barra.x - barra.base) >= anchoMonto + 12
                    : Math.abs(barra.base - barra.y) >= 20 && barra.width >= anchoMonto + 6;

                // Registros afuera
                ctx.fillStyle = '#333333';
                ctx.textAlign = horizontal ? 'left' : 'center';
                ctx.textBaseline = horizontal ? 'middle' : 'bottom';
                ctx.font = negritas;
                if (horizontal) ctx.fillText(registros, barra.x + 6, barra.y);
                else ctx.fillText(registros, barra.x, barra.y - 4);

                // Monto adentro, o afuera junto a los registros si no cabe
                ctx.font = normal;
                if (cabe) {
                    ctx.fillStyle = colorTexto(fondo);
                    if (horizontal) {
                        ctx.textAlign = 'right';
                        ctx.fillText(monto, barra.x - 6, barra.y);
                    } else {
                        ctx.textBaseline = 'top';
                        ctx.fillText(monto, barra.x, barra.y + 4);
                    }
                } else if (horizontal) ctx.fillText(`· ${monto}`, barra.x + 12 + anchoRegistros, barra.y);
                else ctx.fillText(monto, barra.x, barra.y - 19);
            });
        });
        ctx.restore();
    }
};

function InicioSiniestros() {

    const fecha = new Date();
    const { token } = useSelector(state => state.login);
    const classes = useStyles();
    const [alert, setAlert] = useState(null);
    const [empresas, setEmpresas] = useState([]);
    const [filtro, setFiltro] = useState({ ejercicio: fecha.getFullYear(), mes: "0", empresaID: "-1" });
    const [registros, setRegistros] = useState([]);
    const todosLosAnios = !Number(filtro.ejercicio);

    let listAnios = [{ label: 'Todos los ejercicios', value: "0" }];
    for (let anio = fecha.getFullYear() + 1; anio > 2000; anio--) listAnios.push({ label: anio, value: anio });

    const mostrarError = (message) => setAlert({
        descripcion: `Algunos datos necesarios para funcionar no se cargaron correctamente, intenta actualizar la página o verifica tu conexión a internet; a continuación se muestrán más detalles del error: ${NetworkError(message)}`,
        title: "¡Una disculpa!",
        tipo: danger,
        msjConfirmacion: "Aceptar",
        onConfirm: () => setAlert(null)
    });

    useEffect(() => {
        seleccionEmpresa({ filtro: { activa: 1 } }, token)
            .then(lista => setEmpresas([{ label: 'Todas las empresas', value: "-1" }, ...(lista || [])]))
            .catch(({ message }) => mostrarError(message));
    }, []);

    useEffect(() => {
        getInicioSiniestrosGraficas({ ejercicio: Number(filtro.ejercicio) || null, mes: todosLosAnios ? null : Number(filtro.mes) || null, empresaID: filtro.empresaID === "-1" ? null : filtro.empresaID }, token)
            .then(lista => setRegistros(lista || []))
            .catch(({ message }) => mostrarError(message));
    }, [filtro]);

    return (<div className={classes.root}>
        <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
                <Input title="Seleccionar Ejercicio" tipo="autocomplete" id="ejercicio" value={filtro.ejercicio} data={listAnios}
                    onChange={(target) => target && setFiltro({ ...filtro, ejercicio: target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
                {/* Con todos los ejercicios el mes no aplica */}
                <Input title="Seleccionar Mes" tipo="autocomplete" id="mes" value={todosLosAnios ? "0" : filtro.mes} data={listMeses} disabled={todosLosAnios}
                    onChange={(target) => target && setFiltro({ ...filtro, mes: target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <Input title="Seleccionar Empresa" tipo="autocomplete" id="empresa" value={filtro.empresaID} data={empresas}
                    onChange={(target) => target && setFiltro({ ...filtro, empresaID: target.value })} />
            </Grid>
            {GRAFICAS.map(grafica => {
                let { total, monto, data } = datosGrafica(grafica, registros);
                return (<Grid item xs={12} md={6} key={grafica.id}>
                    <Paper className={classes.paper}>
                        <div className={classes.encabezado}>
                            <h5 className={classes.titulo}><b>{`${grafica.titulo} (${total})`}</b></h5>
                            <h5 className={classes.titulo}><b>{pesos(monto)}</b></h5>
                        </div>
                        {total > 0
                            ? <div className={classes.grafica}><Bar data={data} options={opciones(grafica.horizontal)} plugins={[valoresBarras]} /></div>
                            : <div className={classes.sinDatos}>No hay registros en el periodo</div>}
                    </Paper>
                </Grid>);
            })}
        </Grid>
        {alert && <SweetAlert
            title={alert.title}
            descripcion={alert.descripcion}
            tipo={alert.tipo}
            msjConfirmacion={alert.msjConfirmacion}
            msjCancelacion={alert.msjCancelacion}
            onConfirm={alert.onConfirm}
            showConfirm={alert.showConfirm}
            showCancel={alert.showCancel}
            onCancel={() => setAlert(null)}
        />}
    </div>);
}
export default InicioSiniestros;
